// client/src/hooks/useComunicacaoCredito.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const QUERY_KEY = 'comunicacaoCredito';

// 🔹 Saldo atual de créditos da conta logada
export const useSaldoCredito = () => {
    return useQuery({
        queryKey: [QUERY_KEY, 'saldo'],
        queryFn: async () => {
            const { data } = await api.get('/comunicacao-credito/saldo');
            return data.data;
        },
    });
};

// 🔹 Extrato paginado de transações de crédito da conta logada
export const useExtratoCredito = (params) => {
    return useQuery({
        queryKey: [QUERY_KEY, 'extrato', params],
        queryFn: async () => {
            const { data } = await api.get('/comunicacao-credito/extrato', { params });
            return data.data;
        },
    });
};

// 🔹 Pacotes de créditos disponíveis pra compra (só ativos)
export const usePacotesCredito = () => {
    return useQuery({
        queryKey: [QUERY_KEY, 'pacotes'],
        queryFn: async () => {
            const { data } = await api.get('/comunicacao-credito/pacotes');
            return data.data;
        },
    });
};

// 🔹 Comprar um pacote de créditos via cartão
export const useComprarCredito = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await api.post('/comunicacao-credito/comprar', payload);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
            setAlert(data.message || 'Créditos adicionados com sucesso!', 'success');
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao comprar créditos', 'danger');
        },
    });
};

// 🔹 Administração de pacotes (Matriz apenas)
export const usePacotesCreditoAdmin = () => {
    return useQuery({
        queryKey: [QUERY_KEY, 'pacotes', 'admin'],
        queryFn: async () => {
            const { data } = await api.get('/comunicacao-credito/pacotes/admin');
            return data.data;
        },
    });
};

export const useMutatePacoteCredito = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'pacotes'] });
    };

    const criarPacote = useMutation({
        mutationFn: async (payload) => {
            const { data } = await api.post('/comunicacao-credito/pacotes/admin', payload);
            return data.data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote criado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar pacote', 'danger'),
    });

    const atualizarPacote = useMutation({
        mutationFn: async ({ id, ...payload }) => {
            const { data } = await api.put(`/comunicacao-credito/pacotes/admin/${id}`, payload);
            return data.data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote atualizado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar pacote', 'danger'),
    });

    const removerPacote = useMutation({
        mutationFn: async (id) => {
            await api.delete(`/comunicacao-credito/pacotes/admin/${id}`);
            return id;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote removido com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover pacote', 'danger'),
    });

    return { criarPacote, atualizarPacote, removerPacote };
};
